// First Created - 12 Jun 2026
// Author - Atul Pandey
// Purpose - Admin list page for Org Trail Session Cohort setup

import React, { useState } from "react"
import useTitle from "@/hooks/useTitle"
import { addIcon } from "@/assets/img/imgAssets"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline"
import {
  UserGroupIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  IdentificationIcon,
  BuildingOfficeIcon, // 13 Jun 2026
} from "@heroicons/react/24/solid"
import PulseLoader from "react-spinners/PulseLoader"

import {
  useGetOrgTrailSessionCohortsQuery,
  useCreateDefaultCohortsForOrgTrailUsersMutation, // 15 Jun 2026
} from "./orgTrailSessionCohortsApiSlice"

const EtOrgTrailSessionCohortList = ({
  setActiveAdminView,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  // First Created - 12 Jun 2026
  // Author - Atul Pandey
  // Purpose - To list all Org Trail Session Cohorts created through Admin setup
  // Updated - 13 Jun 2026 - added organization details to the view
  // Updated - 14 Jun 2026 - added cohortUserIds
  // Updated - 15 Jun 2026 - grouped cohorts by organization and trail with cohort counts
  // Updated - 15 Jun 2026 - logic for default cohort creation through a button for each org

  useTitle("Cohort List")

  const [showDetails, setShowDetails] = useState({})
  
  // 15 Jun 2026 - Toggle grouped org / trail sections
  const [showOrgGroups, setShowOrgGroups] = useState({})
  const [showTrailGroups, setShowTrailGroups] = useState({})

  const {
    data: orgTrailSessionCohortsData,
    isError,
    error,
    isLoading,
  } = useGetOrgTrailSessionCohortsQuery({
    active: true,
  })

  // 15 Jun 2026 - Create default cohorts for all org + trail combinations
  const [
    createDefaultCohortsForOrgTrailUsers,
    {
        isLoading: isDefaultCohortLoading,
    },
  ] = useCreateDefaultCohortsForOrgTrailUsersMutation()


  const handleToggleDetails = cohortId => {
    setShowDetails(prev => ({
      ...prev,
      [cohortId]: !prev[cohortId],
    }))
  }

  // 15 Jun 2026 - Toggle organization group
  const handleToggleOrgGroup = orgId => {
    setShowOrgGroups(prev => ({
        ...prev,
        [orgId]: !prev[orgId],
    }))
  }

  // 15 Jun 2026 - Toggle trail group within organization
  const handleToggleTrailGroup = (orgId, trailId) => {
    const key = `${orgId}-${trailId}`

    setShowTrailGroups(prev => ({
        ...prev,
        [key]: !prev[key],
    }))
  }

  const formatDate = value => {
    if (!value) return "Not defined"

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) return "Not defined"

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const getUserDisplayName = user => {
    return user?.name || user?.username || user?.email || "User"
  }

  // 15 Jun 2026 - Resolve populated or plain id
  const getRefId = value => {
    if (!value) return "not-defined"
    if (typeof value === "string") return value
    return value?._id || value?.id || "not-defined"
  }

  if (isLoading) return <PulseLoader color={"#ea580c"} />

  if (isError) {
    return (
      <div className="text-sm text-orange-700">
        Error occurred: {error?.data?.message}
      </div>
    )
  }

  const cohorts =
    orgTrailSessionCohortsData?.ids?.map(id => {
      const cohort = orgTrailSessionCohortsData?.entities[id]

      return {
        _id: cohort?._id,
        orgId: cohort?.orgId,
        trailId: cohort?.trailId,
        sessionId: cohort?.sessionId,
        cohortName: cohort?.cohortName,
        cohortCode: cohort?.cohortCode,
        cohortDesc: cohort?.cohortDesc,
        cohortSequence: cohort?.cohortSequence,
        cohortStatus: cohort?.cohortStatus,
        plannedStartDate: cohort?.plannedStartDate,
        plannedEndDate: cohort?.plannedEndDate,
        coachIds: cohort?.coachIds || [],
        sponsorIds: cohort?.sponsorIds || [],
        cohortUserIds: cohort?.cohortUserIds || [], // 14 Jun 2026
        aboutMe: cohort?.aboutMe,
        pic: cohort?.pic,
        active: cohort?.active,
      }
  }) || []

  const sortedCohorts = cohorts
    ?.slice()
    ?.sort((a, b) => {
      const seqA = a?.cohortSequence || 999999
      const seqB = b?.cohortSequence || 999999

      if (seqA !== seqB) return seqA - seqB

      return (a?.cohortCode || a?.cohortName || "").localeCompare(
        b?.cohortCode || b?.cohortName || ""
      )
  })

  // 15 Jun 2026 - Group cohorts by organization and then trail
  const groupedCohortsByOrgTrail = sortedCohorts.reduce((acc, cohort) => {
    const orgId = getRefId(cohort?.orgId)
    const trailId = getRefId(cohort?.trailId)

    if (!acc[orgId]) {
        acc[orgId] = {
        orgId,
        orgInfo: cohort?.orgId,
        cohortCount: 0,
        trails: {},
        }
    }

    if (!acc[orgId].trails[trailId]) {
        acc[orgId].trails[trailId] = {
        trailId,
        trailInfo: cohort?.trailId,
        cohortCount: 0,
        cohorts: [],
        }
    }

    acc[orgId].trails[trailId].cohorts.push(cohort)
    acc[orgId].trails[trailId].cohortCount += 1
    acc[orgId].cohortCount += 1

    return acc
  }, {})

  const groupedOrgEntries = Object.values(groupedCohortsByOrgTrail).sort((a, b) =>
    (a?.orgInfo?.organizationName || "").localeCompare(
        b?.orgInfo?.organizationName || ""
    )
  )


  // 15 Jun 2026 - Trigger MyOrgDefault cohort creation
  const handleCreateDefaultCohorts = async () => {
    try {
        const response = await createDefaultCohortsForOrgTrailUsers().unwrap()

        alert(
        `${response?.message || 'Default cohort creation completed'}\n\n` +
        `Orgs processed: ${response?.orgsProcessed || 0}\n` +
        `Trails processed: ${response?.trailsProcessed || 0}\n` +
        `Cohorts created: ${response?.cohortsCreated || 0}\n` +
        `Cohorts updated: ${response?.cohortsUpdated || 0}\n` +
        `Users added: ${response?.usersAdded || 0}`
        )
    } catch (error) {
        console.error(error)
        alert(error?.data?.message || 'Failed to create default cohorts')
    }
  }

  return (
    <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">

      {/* 1 - Header */}
      <div className="flex justify-between items-center m-1 p-1">
        <div className="flex justify-start items-center">
            <p className="text-2xl m-1 p-1 font-bold">Cohorts</p>

            <img
                src={addIcon}
                alt=""
                className="h-10 w-10 cursor-pointer"
                onClick={onAdd}
            />
        </div>

        {/* 15 Jun 2026 - Create MyOrgDefault cohorts */}

        {isDefaultCohortLoading ? (
            <div className="bg-orange-50 rounded m-1 p-2">
                <p className="text-[10px] text-orange-700">
                Creating / updating MyOrgDefault cohorts...
                </p>
            </div>
            ):(
            <button
                    type="button"
                    onClick={handleCreateDefaultCohorts}
                    disabled={isDefaultCohortLoading}
                    className={`text-[10px] rounded-full mx-2 px-3 py-1 border ${
                        isDefaultCohortLoading
                        ? "border-gray-400 text-gray-400 cursor-not-allowed"
                        : "border-orange-600 text-orange-600 bg-white hover:bg-orange-600 hover:text-white"
                    }`}
                    >
                    {isDefaultCohortLoading ? "Creating..." : "Create Default Cohorts"}
                    </button>
            )
        }

        {/* 15 Jun 2026 - Total cohorts count */}
        <div className="flex justify-start items-center mx-2">
            <p className="text-[10px] text-gray-500 mx-1">Total:</p>

            <div className="rounded-full flex justify-center items-center h-6 w-6 border border-orange-600 bg-white">
            <p className="text-orange-600 text-[10px] font-bold">
                {sortedCohorts?.length || 0}
            </p>
            </div>
        </div>
      </div>

      {/* 2 - Empty State */}
      {sortedCohorts?.length === 0 && (
        <div className="bg-white rounded m-1 p-4">
          <p className="text-sm text-gray-600">
            No cohorts found. Click the add icon to create a cohort for a trail.
          </p>
        </div>
      )}

      {/* 3 - Grouped List - 15 Jun 2026 */}
      {groupedOrgEntries?.map((orgGroup, orgIndex) => {
        const orgId = orgGroup.orgId
        const orgName = orgGroup?.orgInfo?.organizationName || "Organization not defined"
        const trails = Object.values(orgGroup.trails).sort((a, b) =>
            (a?.trailInfo?.trailName || "").localeCompare(
            b?.trailInfo?.trailName || ""
            )
        )

        return (
            <div key={orgId} className="m-1 p-1 bg-white rounded">

            {/* 3a - Organization Group Header */}
            <div className="bg-gray-100 rounded m-1 py-3 p-2 flex justify-between items-center">
                <div className="flex justify-start items-center">
                <div
                    onClick={() => handleToggleOrgGroup(orgId)}
                    className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                >
                    {showOrgGroups[orgId] ? (
                    <MinusIcon className="h-5 w-5 text-orange-600" />
                    ) : (
                    <PlusIcon className="h-5 w-5 text-orange-600" />
                    )}
                </div>

                <BuildingOfficeIcon className="h-5 w-5 text-orange-600 mx-2" />

                <p className="text-sm font-bold">
                    {orgIndex + 1} - {orgName}
                </p>
                </div>

                <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-3 py-1">
                Cohorts: {orgGroup?.cohortCount || 0}
                </p>
            </div>

            {/* 3b - Trail Groups */}
            {showOrgGroups[orgId] && (
                <div className="bg-gray-100 rounded m-1 p-1">
                {trails.map((trailGroup, trailIndex) => {
                    const trailId = trailGroup.trailId
                    const trailKey = `${orgId}-${trailId}`
                    const trailName = trailGroup?.trailInfo?.trailName || "Trail not defined"

                    return (
                    <div key={trailKey} className="bg-white rounded m-1 p-1">

                        {/* Trail Group Header */}
                        <div className="bg-gray-200 rounded m-1 p-2 flex justify-between items-center">
                        <div className="flex justify-start items-center">
                            <div
                            onClick={() => handleToggleTrailGroup(orgId, trailId)}
                            className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                            >
                            {showTrailGroups[trailKey] ? (
                                <MinusIcon className="h-5 w-5 text-orange-600" />
                            ) : (
                                <PlusIcon className="h-5 w-5 text-orange-600" />
                            )}
                            </div>

                            {/* 15 Jun 2026 - Use actual trail pic when available */}
                            <div className="mx-2 flex justify-center items-center rounded-full bg-gray-50 h-7 w-7">
                            {trailGroup?.trailInfo?.pic ? (
                                <img
                                src={trailGroup.trailInfo.pic}
                                alt=""
                                className="h-5 w-5"
                                />
                            ) : (
                                <AcademicCapIcon className="h-5 w-5 text-orange-600" />
                            )}
                            </div>

                            <p className="text-xs font-bold">
                            {trailIndex + 1} - {trailName}
                            </p>
                        </div>

                        <p className="text-[10px] bg-lime-100 text-gray-700 rounded-full mx-2 px-3 py-1">
                            Cohorts: {trailGroup?.cohortCount || 0}
                        </p>
                        </div>

                        {/* Cohorts under Trail */}
                        {showTrailGroups[trailKey] && (
                        <div className="bg-gray-100 rounded m-1 p-1">
                            {trailGroup.cohorts.map((item, index) => {
                            const coaches = item?.coachIds || []
                            const sponsors = item?.sponsorIds || []
                            const cohortUsers = item?.cohortUserIds || []

                            return (
                                <div key={item._id} className="m-1 p-1 bg-white rounded">
                                <div className="m-1 p-1 bg-gray-100 rounded">

                                    {/* Cohort Summary Row */}
                                    <div className="flex justify-between items-center">
                                    <div className="flex justify-start items-center mx-2">

                                        <div
                                        onClick={() => handleToggleDetails(item._id)}
                                        className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                                        >
                                        {showDetails[item._id] ? (
                                            <MinusIcon className="h-6 w-6 text-orange-600" />
                                        ) : (
                                            <PlusIcon className="h-4 w-4 text-orange-600" />
                                        )}
                                        </div>

                                        <div className="m-1 p-1">
                                        <div className="flex justify-start items-center flex-wrap">
                                            <p className="font-bold">
                                            {index + 1} - {item?.cohortName}
                                            </p>

                                            {item?.cohortCode && (
                                            <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                                                {item.cohortCode}
                                            </p>
                                            )}

                                            {item?.cohortStatus && (
                                            <p className="text-[10px] bg-lime-100 text-gray-700 rounded-full mx-2 px-2 py-1">
                                                {item.cohortStatus}
                                            </p>
                                            )}

                                            <p className="text-[10px] bg-gray-200 text-gray-700 rounded-full mx-2 px-2 py-1">
                                            Users: {cohortUsers?.length || 0}
                                            </p>

                                            {!item.active && (
                                            <p className="text-[10px] bg-red-100 text-red-700 rounded-full mx-2 px-2 py-1">
                                                Inactive
                                            </p>
                                            )}
                                        </div>

                                        {item?.cohortDesc && (
                                            <p className="text-[10px] text-gray-600">
                                            {item.cohortDesc}
                                            </p>
                                        )}
                                        </div>
                                    </div>

                                    <div className="flex justify-start items-center mx-2">
                                        <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                        <PencilSquareIcon
                                            onClick={() => onUpdate(item?._id)}
                                            className="h-4 w-4 text-orange-600 cursor-pointer"
                                        />
                                        </div>

                                        <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                                        <TrashIcon
                                            onClick={() => onDelete(item?._id)}
                                            className="h-4 w-4 text-orange-600 cursor-pointer"
                                        />
                                        </div>
                                    </div>
                                    </div>

                                    {/* Existing Details Block */}
                                    {showDetails[item._id] && (
                                    <div className="m-1 p-1 bg-gray-200 rounded">
                                        <div className="m-1 p-1 rounded bg-white">

                                        {/* Organization */}
                                        <div className="flex justify-start items-center my-2">
                                            <BuildingOfficeIcon className="h-4 w-4 text-orange-600 mx-2" />
                                            <p className="text-xs font-bold">
                                            Organization:{" "}
                                            <span className="font-normal text-gray-500">
                                                {item?.orgId?.organizationName || "Not defined"}
                                            </span>
                                            </p>
                                        </div>

                                        {/* Trail */}
                                        <div className="flex justify-start items-center my-2">
                                            <AcademicCapIcon className="h-4 w-4 text-orange-600 mx-2" />
                                            <p className="text-xs font-bold">
                                            Trail:{" "}
                                            <span className="font-normal text-gray-500">
                                                {item?.trailId?.trailName || "Not defined"}
                                            </span>
                                            </p>
                                        </div>

                                        {/* Cohort Code */}
                                        <div className="flex justify-start items-center my-2">
                                            <IdentificationIcon className="h-4 w-4 text-orange-600 mx-2" />
                                            <p className="text-xs font-bold">
                                            Code:{" "}
                                            <span className="font-normal text-gray-500">
                                                {item?.cohortCode || "Not defined"}
                                            </span>
                                            </p>
                                        </div>

                                        {/* Planned Dates */}
                                        <div className="flex justify-start items-center my-2">
                                            <CalendarDaysIcon className="h-4 w-4 text-orange-600 mx-2" />
                                            <p className="text-xs font-bold">
                                            Planned Dates:{" "}
                                            <span className="font-normal text-gray-500">
                                                {formatDate(item?.plannedStartDate)} - {formatDate(item?.plannedEndDate)}
                                            </span>
                                            </p>
                                        </div>

                                        {/* Coaches */}
                                        <div className="my-2">
                                            <div className="flex justify-start items-center">
                                            <UserGroupIcon className="h-4 w-4 text-orange-600 mx-2" />
                                            <p className="text-xs font-bold">
                                                Coaches:{" "}
                                                <span className="font-normal text-gray-500">
                                                {coaches?.length || 0}
                                                </span>
                                            </p>
                                            </div>

                                            {coaches?.length > 0 && (
                                            <div className="flex flex-wrap mx-2 px-2">
                                                {coaches.map((coachItem, coachIndex) => (
                                                <p
                                                    key={coachItem?._id || coachItem?.coachId?._id || coachIndex}
                                                    className="text-[9px] bg-gray-100 text-gray-700 rounded-full m-1 px-2 py-1"
                                                >
                                                    {getUserDisplayName(coachItem?.coachId)}
                                                </p>
                                                ))}
                                            </div>
                                            )}
                                        </div>

                                        {/* Sponsors */}
                                        <div className="my-2">
                                            <div className="flex justify-start items-center">
                                            <UserGroupIcon className="h-4 w-4 text-orange-600 mx-2" />
                                            <p className="text-xs font-bold">
                                                Sponsors:{" "}
                                                <span className="font-normal text-gray-500">
                                                {sponsors?.length || 0}
                                                </span>
                                            </p>
                                            </div>

                                            {sponsors?.length > 0 && (
                                            <div className="flex flex-wrap mx-2 px-2">
                                                {sponsors.map((sponsorItem, sponsorIndex) => (
                                                <p
                                                    key={sponsorItem?._id || sponsorItem?.sponsorId?._id || sponsorIndex}
                                                    className="text-[9px] bg-gray-100 text-gray-700 rounded-full m-1 px-2 py-1"
                                                >
                                                    {getUserDisplayName(sponsorItem?.sponsorId)}
                                                </p>
                                                ))}
                                            </div>
                                            )}
                                        </div>

                                        {/* Cohort Users */}
                                        <div className="my-2">
                                            <div className="flex justify-start items-center">
                                            <UserGroupIcon className="h-4 w-4 text-orange-600 mx-2" />
                                            <p className="text-xs font-bold">
                                                Cohort Users:{" "}
                                                <span className="font-normal text-gray-500">
                                                {cohortUsers?.length || 0}
                                                </span>
                                            </p>
                                            </div>

                                            {cohortUsers?.length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 mx-2 px-2 my-1 max-h-72 overflow-y-auto">
                                                {cohortUsers.map((cohortUserItem, cohortUserIndex) => {
                                                const cohortUser = cohortUserItem?.userId

                                                return (
                                                    <div
                                                    key={
                                                        cohortUserItem?._id ||
                                                        cohortUser?._id ||
                                                        cohortUserIndex
                                                    }
                                                    className="bg-gray-100 rounded m-1 p-2"
                                                    >
                                                    <p className="text-[10px] font-bold text-gray-700">
                                                        {getUserDisplayName(cohortUser)}
                                                    </p>

                                                    {cohortUser?.email && (
                                                        <p className="text-[9px] text-gray-500 break-all">
                                                        {cohortUser.email}
                                                        </p>
                                                    )}

                                                    <div className="flex justify-start items-center flex-wrap mt-1">
                                                        {cohortUserItem?.userCohortStatus && (
                                                        <p className="text-[8px] bg-orange-100 text-orange-700 rounded-full mr-1 px-2 py-1">
                                                            {cohortUserItem.userCohortStatus}
                                                        </p>
                                                        )}

                                                        {cohortUserItem?.active === false && (
                                                        <p className="text-[8px] bg-red-100 text-red-700 rounded-full mr-1 px-2 py-1">
                                                            Inactive
                                                        </p>
                                                        )}
                                                    </div>
                                                    </div>
                                                )
                                                })}
                                            </div>
                                            ) : (
                                            <div className="bg-gray-50 rounded mx-2 px-2 my-1 py-2">
                                                <p className="text-[10px] text-gray-500">
                                                No users added to this cohort yet.
                                                </p>
                                            </div>
                                            )}
                                        </div>

                                        {/* Description */}
                                        {item?.cohortDesc && (
                                            <div className="bg-gray-50 rounded text-xs mx-2 px-2 my-1 py-1">
                                            <p className="m-1 p-1">
                                                {item.cohortDesc}
                                            </p>
                                            </div>
                                        )}

                                        {/* Internal Notes */}
                                        {item?.aboutMe && (
                                            <div className="bg-orange-50 rounded text-xs mx-2 px-2 my-1 py-1">
                                            <p className="font-bold m-1 p-1">Internal Notes</p>
                                            <p className="m-1 p-1">
                                                {item.aboutMe}
                                            </p>
                                            </div>
                                        )}

                                        </div>
                                    </div>
                                    )}
                                </div>
                                </div>
                            )
                            })}
                        </div>
                        )}
                    </div>
                    )
                })}
                </div>
            )}
            </div>
        )
      })}
      
    </div>
  )
}

export default EtOrgTrailSessionCohortList